import React, { useContext, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Symbols,
} from 'recharts';
import { DataAndFilterContext } from '../../context/DataAndFilterContext';

// Tooltip for Scatter Chart
function CustomTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const { avgImportance, avgSatisfaction, job, count } = payload[0].payload;
    return (
      <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: 10 }}>
        <p><strong>Job:</strong> {job}</p>
        <p>Avg Satisfaction: {avgSatisfaction.toFixed(1)}</p>
        <p>Avg Importance: {avgImportance.toFixed(1)}</p>
        <p>Interviews: {count}</p>
      </div>
    );
  }
  return null;
}

/**
 * Standalone chart: "Needs: Importance vs Satisfaction"
 * - Importance & Satisfaction expected on a 0–10 style scale (but axes auto-extend)
 * - Y min = floor(minImportance) - 1 (clamped to 0); Y max = ceil(maxImportance) + 1
 * - X min = floor(minSatisfaction) - 1 (clamped to 0); X max = ceil(maxSatisfaction) + 1
 * - All stars are rendered at a constant size (no ZAxis scaling)
 */
export default function NeedsImportanceSatisfactionScatter() {
  const { filteredData } = useContext(DataAndFilterContext);

  // Build scatter data: one dot per Job category with averages & interview count
  const scatterData = useMemo(() => {
    const jobStats = {};
    (filteredData || []).forEach((file) => {
      (file.jobs_to_be_done || []).forEach((job) => {
        const category = job.job_to_be_done_category;
        if (!category) return;

        const imp = Number(job.importance);
        const sat = Number(job.satisfaction);

        if (!jobStats[category]) {
          jobStats[category] = { sumImp: 0, sumSat: 0, n: 0 };
        }
        jobStats[category].sumImp += Number.isFinite(imp) ? imp : 0;
        jobStats[category].sumSat += Number.isFinite(sat) ? sat : 0;
        jobStats[category].n += 1;
      });
    });

    return Object.entries(jobStats).map(([job, { sumImp, sumSat, n }]) => ({
      job,
      avgImportance: n ? sumImp / n : 0,
      avgSatisfaction: n ? sumSat / n : 0,
      count: n,
    }));
  }, [filteredData]);

  // Compute dynamic axis min/max
  const { xMin, xMax, yMin, yMax } = useMemo(() => {
    if (!scatterData.length) return { xMin: 0, xMax: 1, yMin: 0, yMax: 1 };
    const minImp = Math.min(...scatterData.map((d) => d.avgImportance));
    const maxImp = Math.max(...scatterData.map((d) => d.avgImportance));
    const minSat = Math.min(...scatterData.map((d) => d.avgSatisfaction));
    const maxSat = Math.max(...scatterData.map((d) => d.avgSatisfaction));

    const y0 = Math.max(0, Math.floor(minImp) - 1);
    const y1 = Math.ceil(maxImp) + 1;
    const x0 = Math.max(0, Math.floor(minSat) - 1);
    const x1 = Math.ceil(maxSat) + 1;

    // Ensure min < max
    return {
      xMin: Math.min(x0, x1 - 1),
      xMax: Math.max(x1, x0 + 1),
      yMin: Math.min(y0, y1 - 1),
      yMax: Math.max(y1, y0 + 1),
    };
  }, [scatterData]);

  const makeTicks = (start, end) => {
    const s = Math.floor(start);
    const e = Math.ceil(end);
    const n = Math.max(2, e - s + 1);
    return Array.from({ length: n }, (_, i) => s + i);
  };

  if (!scatterData.length) {
    return (
      <Box>
        <Typography variant="h6">Needs: Importance vs Satisfaction</Typography>
        <Typography variant="body2" color="text.secondary">No data to display.</Typography>
      </Box>
    );
  }

  // Fixed-size star symbol for all points
  const Star = (props) => <Symbols {...props} type="star" size={110} />; // tweak size if desired

  return (
    <Box>
      <Typography variant="h6">Needs: Importance vs Satisfaction</Typography>
      <Typography variant="subtitle1">
        The importance vs satisfaction of the Jobs To Be Done
      </Typography>

      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart>
          <CartesianGrid />
          <XAxis
            type="number"
            dataKey="avgSatisfaction"
            name="Satisfaction"
            domain={[xMin, xMax]}
            ticks={makeTicks(xMin, xMax)}
            label={{ value: 'Satisfaction', position: 'insideBottom', offset: -5 }}
          />
          <YAxis
            type="number"
            dataKey="avgImportance"
            name="Importance"
            domain={[yMin, yMax]}
            ticks={makeTicks(yMin, yMax)}
            label={{ value: 'Importance', angle: -90, position: 'insideLeft', offset: 0 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Scatter name="Job Categories" data={scatterData} fill="#8884d8" shape={Star} />
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  );
}
